/**
 * JWT Authentication utilities for catalog-blog
 * Integrates with Genkan auth service for cross-domain authentication
 */

export interface ExtenshiJWTPayload {
	userId: string
	email: string
	role: string
	permissions: string[]
	iat: number
	exp: number
	iss: string
	aud: string
}

export interface UserDataCookie {
	userId: string
	email: string
	role: string
	permissions: string[]
	expiresAt: number
}

/**
 * Cookie names for JWT tokens (must match Genkan service)
 */
export const COOKIE_NAMES = {
	AUTH_TOKEN: 'extenshi-auth-token',
	REFRESH_TOKEN: 'extenshi-refresh-token',
	USER_DATA: 'extenshi-user-data',
} as const

/**
 * LocalStorage keys for development mode (localhost port-specific cookies workaround)
 */
export const LOCAL_STORAGE_KEYS = {
	AUTH_TOKEN: 'extenshi_local_auth_token',
	USER_DATA: 'extenshi_local_user_data',
} as const

/**
 * Check if we're in local development environment
 */
function isLocalDev(): boolean {
	if (typeof window === 'undefined') return false
	const hostname = window.location.hostname
	return hostname === 'localhost' || hostname === '127.0.0.1'
}

/**
 * Get cookie value by name (client-side)
 */
function getCookie(name: string): string | null {
	if (typeof document === 'undefined') return null

	const value = `; ${document.cookie}`
	const parts = value.split(`; ${name}=`)
	if (parts.length === 2) {
		return parts.pop()?.split(';').shift() || null
	}
	return null
}

/**
 * Get value from localStorage (for local dev)
 */
function getLocalStorage(key: string): string | null {
	if (typeof window === 'undefined') return null
	try {
		return localStorage.getItem(key)
	} catch {
		return null
	}
}

/**
 * Set value to localStorage (for local dev)
 */
function setLocalStorage(key: string, value: string): void {
	if (typeof window === 'undefined') return
	try {
		localStorage.setItem(key, value)
	} catch {
		console.error('Failed to set localStorage')
	}
}

/**
 * Remove value from localStorage (for local dev)
 */
function removeLocalStorage(key: string): void {
	if (typeof window === 'undefined') return
	try {
		localStorage.removeItem(key)
	} catch {
		console.error('Failed to remove from localStorage')
	}
}

/**
 * Extract auth tokens from URL parameters (for local dev redirect)
 * Returns true if tokens were found and saved
 */
export function extractAndSaveAuthFromUrl(): boolean {
	if (typeof window === 'undefined') return false
	if (!isLocalDev()) return false

	const url = new URL(window.location.href)
	const authToken = url.searchParams.get('_authToken')
	const userData = url.searchParams.get('_userData')

	if (authToken && userData) {
		// Save to localStorage
		setLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN, authToken)
		setLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA, userData)

		// Remove params from URL (clean up)
		url.searchParams.delete('_authToken')
		url.searchParams.delete('_userData')
		window.history.replaceState({}, '', url.toString())

		console.debug('Auth tokens extracted from URL and saved to localStorage')
		return true
	}

	return false
}

/**
 * Decode base64url to string (browser-compatible)
 */
function decodeBase64Url(base64url: string): string {
	// Convert base64url to base64
	let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')

	// Add padding if needed
	const padding = base64.length % 4
	if (padding) {
		base64 += '='.repeat(4 - padding)
	}

	// Decode using atob (browser-compatible)
	return decodeURIComponent(
		atob(base64)
			.split('')
			.map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
			.join(''),
	)
}

/**
 * Parse user data cookie (quick client-side access)
 * Checks both cookies and localStorage (for local dev)
 */
export function parseUserDataCookie(cookieValue?: string): UserDataCookie | null {
	try {
		// First check cookie, then localStorage (for local dev)
		let value = cookieValue || getCookie(COOKIE_NAMES.USER_DATA)

		// For local dev, also check localStorage
		if (!value && isLocalDev()) {
			value = getLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA)
			if (value) {
				console.debug('User data found in localStorage (local dev)')
			}
		}

		if (!value) {
			return null
		}

		const decoded = decodeBase64Url(value)
		const userData = JSON.parse(decoded) as UserDataCookie

		// Check if expired
		if (userData.expiresAt < Date.now()) {
			// Clear localStorage if expired
			if (isLocalDev()) {
				removeLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA)
				removeLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN)
			}
			return null
		}

		return userData
	} catch (error) {
		console.error('Failed to parse user data:', error)
		return null
	}
}

/**
 * Get JWT token from cookies or localStorage (for local dev)
 */
export function getJWTToken(): string | null {
	// First check cookie
	let token = getCookie(COOKIE_NAMES.AUTH_TOKEN)

	// For local dev, also check localStorage
	if (!token && isLocalDev()) {
		token = getLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN)
		if (token) {
			console.debug('Auth token found in localStorage (local dev)')
		}
	}

	return token
}

/**
 * Verify JWT token with Genkan auth service
 * For cross-domain auth, we call the verify endpoint with credentials: 'include'
 * so that genkan can read its own cookies from the request
 */
export async function verifyJWTWithAuthService(token?: string): Promise<ExtenshiJWTPayload | null> {
	try {
		const tokenToVerify = token || getJWTToken()
		const authServiceUrl = process.env.NEXT_PUBLIC_AUTH_URL || 'http://localhost:3005'

		// Skip verification if no token and we're in local dev without auth service
		if (!tokenToVerify && isLocalDev()) {
			console.debug('No token available for verification in local dev')
			return null
		}

		// If we have a local token, send it in the body
		// Otherwise, let genkan read the token from its own cookies via credentials: 'include'
		const response = await fetch(`${authServiceUrl}/api/jwt/verify`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			credentials: 'include',
			body: JSON.stringify({ token: tokenToVerify || undefined }),
		})

		if (!response.ok) {
			console.debug('JWT verification failed:', response.status)
			return null
		}

		const data = await response.json()

		if (data.valid && data.user) {
			return {
				userId: data.user.id,
				email: data.user.email,
				role: data.user.role,
				permissions: data.user.permissions,
				iat: Math.floor(data.issuedAt / 1000),
				exp: Math.floor(data.expiresAt / 1000),
				iss: 'extenshi-auth',
				aud: 'extenshi.io',
			}
		}

		return null
	} catch (error) {
		// Network errors (auth service unavailable) are expected in dev - log as debug, not error
		if (error instanceof TypeError && error.message === 'Failed to fetch') {
			console.debug('Auth service unavailable - running in unauthenticated mode')
		} else {
			console.debug('JWT verification error:', error)
		}
		return null
	}
}

/**
 * Refresh JWT tokens via auth service
 */
export async function refreshJWTTokens(): Promise<boolean> {
	try {
		const authServiceUrl = process.env.NEXT_PUBLIC_AUTH_URL || 'http://localhost:3005'

		const response = await fetch(`${authServiceUrl}/api/jwt/refresh`, {
			method: 'POST',
			credentials: 'include',
		})

		if (response.ok) {
			console.log('JWT tokens refreshed successfully')
			return true
		}

		console.debug('Token refresh failed:', response.status)
		return false
	} catch (error) {
		console.error('Error refreshing tokens:', error)
		return false
	}
}

/**
 * Sign out user by revoking tokens
 */
export async function signOutJWT(): Promise<boolean> {
	try {
		// Clear localStorage for local dev
		if (isLocalDev()) {
			removeLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN)
			removeLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA)
		}

		const authServiceUrl = process.env.NEXT_PUBLIC_AUTH_URL || 'http://localhost:3005'

		const response = await fetch(`${authServiceUrl}/api/jwt/refresh`, {
			method: 'DELETE',
			credentials: 'include',
		})

		return response.ok
	} catch (error) {
		console.error('Error signing out:', error)
		// Still clear localStorage even if remote signout fails
		if (isLocalDev()) {
			removeLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN)
			removeLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA)
		}
		return false
	}
}

/**
 * Get auth service redirect URL for login
 */
export function getAuthLoginUrl(returnUrl?: string): string {
	const authServiceUrl = process.env.NEXT_PUBLIC_AUTH_URL || 'http://localhost:3005'
	const currentUrl = typeof window !== 'undefined' ? window.location.href : ''
	const redirectUrl = returnUrl || currentUrl

	const params = new URLSearchParams()
	if (redirectUrl) {
		params.set('redirect', redirectUrl)
	}

	return `${authServiceUrl}/auth/login${params.toString() ? `?${params.toString()}` : ''}`
}

/**
 * Redirect to auth service for login
 */
export function redirectToLogin(returnUrl?: string): void {
	const loginUrl = getAuthLoginUrl(returnUrl)
	window.location.href = loginUrl
}

/**
 * Check if user data is still valid
 */
export function isUserDataValid(userData: UserDataCookie | null): boolean {
	return userData !== null && userData.expiresAt > Date.now()
}

/**
 * Get user display name
 */
export function getUserDisplayName(userData: UserDataCookie | null): string {
	if (!userData) return 'Guest'

	// Extract name from email if available
	const emailName = userData.email.split('@')[0]
	return emailName.charAt(0).toUpperCase() + emailName.slice(1)
}

/**
 * Format user role for display
 */
export function formatUserRole(role: string): string {
	return role.charAt(0).toUpperCase() + role.slice(1)
}
